import { Disclosure } from "./disclosure";
import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";

/**
 * Small SVG charts for the extranets: grouped or stacked bars and lines, one y axis, a legend,
 * a hover tooltip per category and the same numbers as a table. Colors come from the
 * --series-* tokens in styles.css, validated for colour blindness in light and dark mode.
 */

export interface ChartSeries {
  label: string;
  /** CSS color, normally a token: var(--series-1). */
  color: string;
  /** One value per category; null leaves a gap (a month that has not started, for instance). */
  values: (number | null)[];
}

interface ChartProps {
  categories: string[];
  series: ChartSeries[];
  /** Value shown in the tooltip and the table. */
  format: (v: number) => string;
  /** Value on the y axis; short, like €40k. */
  axisFormat?: (v: number) => string;
  /** Long name of a category for the tooltip and the table ("March 2026" for "Mar"). */
  categoryTitle?: (i: number) => string;
  height?: number;
  /** What the chart shows, for screen readers. */
  label: string;
}

const PAD = { top: 10, right: 8, bottom: 24, left: 52 };

function useWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(600);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const fit = (w: number) => setWidth(Math.max(280, Math.floor(w)));
    // Measured before the first paint, so the chart never shows at a guessed width; then on every resize.
    fit(ref.current.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => entry && fit(entry.contentRect.width));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** Axis ticks in steps of 1, 2, 2.5 or 5 times a power of ten, about four of them, from `min` (rounded down) to `max` (rounded up). */
function niceScale(max: number, min = 0): number[] {
  if (max <= min) return [min, min + 1];
  const raw = (max - min) / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 2.5, 5, 10].find((m) => m * pow >= raw) ?? 10) * pow;
  const from = Math.floor(min / step) * step;
  return Array.from({ length: Math.ceil((max - from) / step) + 1 }, (_, i) => from + i * step);
}

/** Bar with rounded top corners, anchored to the baseline. */
function barPath(x: number, y: number, w: number, h: number, round: boolean): string {
  const r = round ? Math.min(4, w / 2, h) : 0;
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

export function ChartLegend({ series }: { series: Pick<ChartSeries, "label" | "color">[] }) {
  return (
    <div className="chart-legend">
      {series.map((s) => <span key={s.label}><i style={{ background: s.color }} />{s.label}</span>)}
    </div>
  );
}

function Frame({ props, width, ticks, plot, hover, setHover, children }: {
  props: ChartProps; width: number; ticks: number[]; plot: { w: number; h: number; band: number };
  hover: number | null; setHover: (i: number | null) => void; children: ReactNode;
}) {
  const { categories, series, format, axisFormat = format, categoryTitle = (i) => categories[i]!, height = 220 } = props;
  // Every nth label on the x axis, so that labels of about 6.5px per character never touch.
  const labelEvery = Math.max(1, Math.ceil((Math.max(...categories.map((c) => c.length)) * 6.5 + 10) / plot.band));
  const [low, top] = [ticks[0]!, ticks.at(-1)!];
  const y = (v: number) => PAD.top + plot.h - ((v - low) / (top - low)) * plot.h;
  const tipLeft = hover === null ? 0 : Math.min(Math.max(PAD.left + plot.band * (hover + 0.5), 90), width - 90);
  return (
    <>
      <svg width={width} height={height} role="img" aria-label={props.label} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} className={t === low ? "chart-base" : "chart-grid"} />
            <text x={PAD.left - 8} y={y(t)} dy="0.32em" textAnchor="end" className="chart-axis">{axisFormat(t)}</text>
          </g>
        ))}
        {categories.map((c, i) => i % labelEvery === 0 && (
          <text key={i} x={PAD.left + plot.band * (i + 0.5)} y={height - 6} textAnchor="middle" className="chart-axis">{c}</text>
        ))}
        {children}
        {categories.map((_, i) => (
          <rect key={i} x={PAD.left + plot.band * i} y={PAD.top} width={plot.band} height={plot.h} fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
      {hover !== null && (
        <div className="chart-tip" style={{ left: tipLeft }}>
          <div className="chart-tip-title">{categoryTitle(hover)}</div>
          {series.map((s) => (
            <div key={s.label} className="chart-tip-row"><i style={{ background: s.color }} /><span>{s.label}</span><b>{s.values[hover] === null ? "—" : format(s.values[hover]!)}</b></div>
          ))}
        </div>
      )}
    </>
  );
}

/** The chart's numbers as a table, folded under the chart. */
function ChartTable({ categories, series, format, categoryTitle = (i) => categories[i]! }: ChartProps) {
  return (
    <Disclosure className="chart-table" triggerClassName="disclosure-toggle" trigger="Show as table">
      <table className="table">
        <thead><tr><th />{series.map((s) => <th key={s.label} style={{ textAlign: "right" }}>{s.label}</th>)}</tr></thead>
        <tbody>{categories.map((_, i) => (
          <tr key={i}><td>{categoryTitle(i)}</td>{series.map((s) => <td key={s.label} style={{ textAlign: "right" }}>{s.values[i] === null ? "—" : format(s.values[i]!)}</td>)}</tr>
        ))}</tbody>
      </table>
    </Disclosure>
  );
}

/** Bars per category, side by side or stacked (stacked series add up to the category total). */
export function BarChart(props: ChartProps & { stacked?: boolean }) {
  const { categories, series, stacked, height = 220 } = props;
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(0, ...categories.map((_, i) => stacked ? series.reduce((s, x) => s + (x.values[i] ?? 0), 0) : Math.max(0, ...series.map((x) => x.values[i] ?? 0))));
  const ticks = niceScale(max);
  const plot = { w: width - PAD.left - PAD.right, h: height - PAD.top - PAD.bottom, band: (width - PAD.left - PAD.right) / categories.length };
  const scale = plot.h / ticks.at(-1)!;
  const groupW = plot.band * 0.72;
  const barW = stacked ? Math.min(groupW, 28) : Math.min((groupW - 2 * (series.length - 1)) / series.length, 16);

  const bars: ReactNode[] = [];
  categories.forEach((_, i) => {
    const center = PAD.left + plot.band * (i + 0.5);
    if (stacked) {
      const parts = series.map((s) => ({ s, v: s.values[i] ?? 0 })).filter((p) => p.v > 0);
      let base = PAD.top + plot.h;
      parts.forEach(({ s, v }, k) => {
        const h = Math.max(1, v * scale - (k > 0 ? 2 : 0));
        base -= h + (k > 0 ? 2 : 0);
        bars.push(<path key={`${i}-${s.label}`} d={barPath(center - barW / 2, base, barW, h, k === parts.length - 1)} fill={s.color} opacity={hover === null || hover === i ? 1 : 0.55} />);
      });
    } else {
      const left = center - (barW * series.length + 2 * (series.length - 1)) / 2;
      series.forEach((s, k) => {
        const v = s.values[i];
        if (!v) return;
        const h = Math.max(1, v * scale);
        bars.push(<path key={`${i}-${s.label}`} d={barPath(left + k * (barW + 2), PAD.top + plot.h - h, barW, h, true)} fill={s.color} opacity={hover === null || hover === i ? 1 : 0.55} />);
      });
    }
  });

  return (
    <div className="chart">
      <ChartLegend series={series} />
      <div ref={ref} className="chart-plot"><Frame props={props} width={width} ticks={ticks} plot={plot} hover={hover} setHover={setHover}>{bars}</Frame></div>
      <ChartTable {...props} />
    </div>
  );
}

/** One line per series, with a marker on each value; nulls break the line. The axis starts near the lowest value, not at zero. */
export function LineChart(props: ChartProps) {
  const { categories, series, height = 220 } = props;
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const values = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  const ticks = niceScale(Math.max(0, ...values), values.length ? Math.min(...values) * 0.8 : 0);
  const plot = { w: width - PAD.left - PAD.right, h: height - PAD.top - PAD.bottom, band: (width - PAD.left - PAD.right) / categories.length };
  const x = (i: number) => PAD.left + plot.band * (i + 0.5);
  const y = (v: number) => PAD.top + plot.h - ((v - ticks[0]!) / (ticks.at(-1)! - ticks[0]!)) * plot.h;

  return (
    <div className="chart">
      <ChartLegend series={series} />
      <div ref={ref} className="chart-plot">
        <Frame props={props} width={width} ticks={ticks} plot={plot} hover={hover} setHover={setHover}>
          {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plot.h} className="chart-cross" />}
          {series.map((s) => {
            const d = s.values.map((v, i) => (v === null ? "" : `${i === 0 || s.values[i - 1] === null ? "M" : "L"}${x(i)},${y(v)}`)).join("");
            return (
              <g key={s.label}>
                <path d={d} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" />
                {s.values.map((v, i) => v !== null && <circle key={i} cx={x(i)} cy={y(v)} r={hover === i ? 5 : 3.5} fill={s.color} className="chart-dot" />)}
              </g>
            );
          })}
        </Frame>
      </div>
      <ChartTable {...props} />
    </div>
  );
}
